import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import '../styles/admin-pending.css';
import { useDataSync, useRouteRefresh } from '../context/DataSyncContext';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import type { Flow, FlowStatus } from '../types';

export function AdminPendingPage() {
  const { user } = useAuth();
  const location = useLocation();
  const [flows, setFlows] = useState<Flow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFlow, setSelectedFlow] = useState<string | null>(null);
  const [statusView, setStatusView] = useState<FlowStatus>('pending');
  const { refreshAllBadges } = useDataSync();
  const refreshToken = useRouteRefresh('/admin/pending');
  const autoRefreshTick = useAutoRefresh(5000, { enabled: user?.role === 'admin' });
  const requestIdRef = useRef(0);
  const pendingScrollRef = useRef<number | null>(null);
  const scrollPersistedRef = useRef(false);
  const viewLabel = statusView === 'pending' ? 'Pending submissions' : 'Rejected submissions';
  const listingLinkState = useMemo(
    () => ({
      from: {
        pathname: location.pathname,
        search: location.search,
        label: viewLabel
      }
    }),
    [location.pathname, location.search, viewLabel]
  );

  useEffect(() => {
    const stored = sessionStorage.getItem('admin-pending:scroll');
    if (stored) {
      const parsed = Number(stored);
      if (!Number.isNaN(parsed)) {
        pendingScrollRef.current = parsed;
      }
      sessionStorage.removeItem('admin-pending:scroll');
    }
    scrollPersistedRef.current = false;
  }, []);

  const persistScrollPosition = useCallback(() => {
    try {
      sessionStorage.setItem('admin-pending:scroll', String(window.scrollY));
    } catch (err) {
      console.warn('Unable to store admin pending scroll position', err);
    }
    scrollPersistedRef.current = true;
  }, []);

  const restorePendingScroll = useCallback(() => {
    const target = pendingScrollRef.current;
    if (target === null) {
      return;
    }
    const scrollNow = () => window.scrollTo({ top: target });
    requestAnimationFrame(() => {
      scrollNow();
      requestAnimationFrame(scrollNow);
    });
    pendingScrollRef.current = null;
    scrollPersistedRef.current = false;
    sessionStorage.removeItem('admin-pending:scroll');
  }, []);

  useEffect(
    () => () => {
      if (!scrollPersistedRef.current) {
        persistScrollPosition();
      }
    },
    [persistScrollPosition]
  );

  const fetchFlows = useCallback(
    async ({ silent = false } = {}) => {
      if (user?.role !== 'admin') {
        if (!silent) {
          setFlows([]);
          setLoading(false);
          setError(null);
        }
        return;
      }

      const requestId = ++requestIdRef.current;

      if (!silent) {
        setLoading(true);
        setError(null);
      }

      try {
        const payload = await api.flows({ status: statusView });

        if (requestIdRef.current !== requestId) {
          return;
        }

        setFlows(payload);
        if (statusView === 'pending') {
          void refreshAllBadges();
        }
        restorePendingScroll();
      } catch (err) {
        if (requestIdRef.current !== requestId) {
          return;
        }

        if (silent) {
          console.error('Admin pending background refresh failed', err);
        } else {
          setError((err as Error).message);
        }
      } finally {
        if (requestIdRef.current !== requestId) {
          return;
        }

        if (!silent) {
          setLoading(false);
        }
      }
    },
    [refreshAllBadges, restorePendingScroll, statusView, user?.role]
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void fetchFlows();
    }, 150);

    return () => {
      window.clearTimeout(timer);
    };
  }, [fetchFlows, refreshToken]);

  useEffect(() => {
    if (!autoRefreshTick || user?.role !== 'admin') {
      return;
    }
    void fetchFlows({ silent: true });
  }, [autoRefreshTick, fetchFlows, user?.role]);

  const handleApprove = async (flowId: string) => {
    setSelectedFlow(flowId);
    try {
      await api.approveFlow(flowId);
      setFlows((current) => current.filter((flow) => flow.id !== flowId));
      void refreshAllBadges();
    } catch (approveError) {
      setError((approveError as Error).message);
    } finally {
      setSelectedFlow(null);
    }
  };

  if (user?.role !== 'admin') {
    return <div className="alert alert-error">Admin privileges are required.</div>;
  }

  return (
    <div className="pending">
      <div className="pending__header">
        <div>
          <h1>{viewLabel}</h1>
          <p>
            {statusView === 'pending'
              ? 'Review and approve new or updated flows submitted by contributors.'
              : 'Review rejected submissions and monitor follow-up comments from authors.'}
          </p>
        </div>
        <div className="pending__actions">
          <div className="pending__toggle">
            <button
              type="button"
              className={`chip ${statusView === 'pending' ? 'chip-selected' : ''}`}
              onClick={() => setStatusView('pending')}
            >
              Pending
            </button>
            <button
              type="button"
              className={`chip ${statusView === 'rejected' ? 'chip-selected' : ''}`}
              onClick={() => setStatusView('rejected')}
            >
              Rejected
            </button>
          </div>
          <span className="pending__count">{flows.length} items</span>
        </div>
      </div>

      {error ? <div className="alert alert-error">{error}</div> : null}

      {loading ? (
        <div className="loading">Loading submissions…</div>
      ) : flows.length === 0 ? (
        <div className="empty-state">
          <h3>{statusView === 'pending' ? 'No flows waiting for approval' : 'No rejected submissions'}</h3>
          <p>
            {statusView === 'pending'
              ? 'Great job! Everything in the marketplace is up to date.'
              : 'No entries are currently rejected. Switch back to pending to continue reviews.'}
          </p>
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Author</th>
              <th>Updated</th>
              <th>Kind</th>
              <th>Flow type</th>
              <th>Tags</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {flows.map((flow) => (
              <tr key={flow.id}>
                <td>
                  <Link
                    to={`/flows/${flow.id}`}
                    state={listingLinkState}
                    onClickCapture={persistScrollPosition}
                  >
                    {flow.title}
                  </Link>
                </td>
                <td>{flow.createdByName}</td>
                <td>{new Date(flow.updatedAt).toLocaleString()}</td>
                <td>{flow.kind === 'node' ? 'Node' : 'Flow'}</td>
                <td>
                  {flow.flowType ? flow.flowType : '—'}
                  {flow.flowType && flow.flowTypeDetail ? ` — ${flow.flowTypeDetail}` : ''}
                </td>
                <td>{flow.tags.join(', ')}</td>
                <td>
                  <div className="table-actions">
                    <Link
                      className="button button-tertiary"
                      to={`/flows/${flow.id}`}
                      state={listingLinkState}
                      onClickCapture={persistScrollPosition}
                    >
                      Review
                    </Link>
                    <button
                      className="button button-secondary"
                      type="button"
                      onClick={() => handleApprove(flow.id)}
                      disabled={selectedFlow === flow.id}
                    >
                      {selectedFlow === flow.id ? 'Approving…' : 'Approve'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
